import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute } from "@tanstack/react-router"
import { Pencil, Power, ShieldCheck, ShieldOff, UserPlus } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { type Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { AdminUser } from "@/api/generated/AdminUser"
import { useAdminUsers, useCreateAdminUser, useUpdateAdminUser } from "@/api/queries/admin-users"
import { useSession } from "@/api/queries/session"
import { createColumns, DataTable } from "@/components/app/data-table"
import { SwitchField, TextField } from "@/components/app/fields"
import { FormSheet } from "@/components/app/form"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { StatCard } from "@/components/app/stat-card"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { UserAvatar } from "@/components/app/user-avatar"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { formatNumber } from "@/lib/format"
import { requireCap } from "@/lib/route-guards"
import { z } from "@/lib/zod"

export const Route = createFileRoute("/_app/settings/platform-users")({
  staticData: { titleKey: "nav.items.platformUsers" },
  beforeLoad: requireCap("platformAdmin"),
  component: PlatformUsersPage,
})

function PlatformUsersPage() {
  const { t } = useTranslation()
  const session = useSession()
  const users = useAdminUsers()
  const update = useUpdateAdminUser()
  const [editing, setEditing] = useState<AdminUser | undefined>()
  const [open, setOpen] = useState(false)
  const openEdit = (u: AdminUser) => {
    setEditing(u)
    setOpen(true)
  }
  const openCreate = () => {
    setEditing(undefined)
    setOpen(true)
  }

  const stats = useMemo(() => {
    const list = users.data ?? []
    return {
      total: list.length,
      active: list.filter((u) => u.is_active).length,
      admins: list.filter((u) => u.is_platform_admin).length,
    }
  }, [users.data])

  const columns = useMemo(
    () =>
      createColumns<AdminUser>((c) => [
        c.accessor((u) => `${u.display_name} ${u.username} ${u.email ?? ""}`, {
          id: "user",
          header: t("platformUsers.columns.user"),
          meta: { label: t("platformUsers.columns.user") },
          enableHiding: false,
          sortFn: "text",
          cell: ({ row }) => {
            const u = row.original
            return (
              <div className="flex min-w-0 items-center gap-2.5">
                <UserAvatar name={u.display_name || u.username} seed={u.username} size="sm" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{u.display_name || u.username}</div>
                  <div className="text-muted-foreground truncate text-xs">
                    @{u.username}
                    {u.email ? ` · ${u.email}` : ""}
                  </div>
                </div>
              </div>
            )
          },
        }),
        c.accessor("is_platform_admin", {
          header: t("platformUsers.columns.role"),
          cell: ({ getValue }) =>
            getValue() ? (
              <StatusBadge tone="accent">
                <ShieldCheck />
                {t("members.platformAdmin")}
              </StatusBadge>
            ) : (
              <span className="text-muted-foreground">{t("platformUsers.user")}</span>
            ),
        }),
        c.accessor("is_active", {
          header: t("platformUsers.columns.status"),
          cell: ({ getValue }) => (
            <StatusBadge tone={getValue() ? "success" : "neutral"} dot>
              {getValue() ? t("status.active") : t("status.inactive")}
            </StatusBadge>
          ),
        }),
        c.accessor("tenant_count", {
          header: t("platformUsers.columns.organizations"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("last_login_at", {
          header: t("platformUsers.columns.lastLogin"),
          sortFn: "datetime",
          sortUndefined: "last",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
        c.accessor("created_at", {
          header: t("platformUsers.columns.created"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("platformUsers.title")}
        description={t("platformUsers.description")}
        icon={ShieldCheck}
        actions={
          <Button onClick={openCreate}>
            <UserPlus />
            {t("platformUsers.create")}
          </Button>
        }
      />
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <StatCard
          label={t("platformUsers.stats.total")}
          value={formatNumber(stats.total)}
          loading={users.isPending}
        />
        <StatCard
          label={t("platformUsers.stats.active")}
          value={formatNumber(stats.active)}
          loading={users.isPending}
        />
        <StatCard
          label={t("platformUsers.stats.admins")}
          value={formatNumber(stats.admins)}
          loading={users.isPending}
        />
      </div>
      <DataTable
        label={t("platformUsers.title")}
        data={users.data}
        columns={columns}
        getRowId={(u) => String(u.id)}
        loading={users.isPending}
        error={users.error}
        onRetry={() => void users.refetch()}
        searchable
        searchPlaceholder={t("platformUsers.search")}
        columnToggle
        persistKey="platform-users"
        initialSorting={[{ id: "user", desc: false }]}
        onRowClick={openEdit}
        rowActions={(u) => {
          const self = u.id === session.user?.id
          return (
            <>
              <DropdownMenuItem onClick={() => openEdit(u)}>
                <Pencil />
                {t("common.edit")}
              </DropdownMenuItem>
              {!self && (
                <DropdownMenuItem onClick={() => update.mutate({ id: u.id, is_active: !u.is_active })}>
                  <Power />
                  {u.is_active ? t("members.deactivate") : t("members.activate")}
                </DropdownMenuItem>
              )}
              {!self && (
                <DropdownMenuItem
                  onClick={() => update.mutate({ id: u.id, is_platform_admin: !u.is_platform_admin })}
                >
                  {u.is_platform_admin ? <ShieldOff /> : <ShieldCheck />}
                  {u.is_platform_admin ? t("platformUsers.revokeAdmin") : t("platformUsers.grantAdmin")}
                </DropdownMenuItem>
              )}
            </>
          )
        }}
        emptyState={<EmptyState variant="plain" icon={ShieldCheck} title={t("platformUsers.empty")} />}
      />
      <UserSheet open={open} onOpenChange={setOpen} user={editing} selfId={session.user?.id ?? null} />
    </PageContainer>
  )
}

const schema = z.object({
  username: z.string().trim().min(1).max(150),
  display_name: z.string().trim().max(150),
  email: z.union([z.literal(""), z.email()]),
  password: z.union([z.literal(""), z.string().min(10)]),
  is_active: z.boolean(),
  is_platform_admin: z.boolean(),
})
type Values = z.infer<typeof schema>

function userDefaults(u?: AdminUser): Values {
  return {
    username: u?.username ?? "",
    display_name: u?.display_name ?? "",
    email: u?.email ?? "",
    password: "",
    is_active: u?.is_active ?? true,
    is_platform_admin: u?.is_platform_admin ?? false,
  }
}

function UserSheet({
  open,
  onOpenChange,
  user,
  selfId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  user?: AdminUser
  selfId: number | null
}) {
  const { t } = useTranslation()
  const create = useCreateAdminUser()
  const update = useUpdateAdminUser()
  const editing = !!user
  const self = user?.id === selfId
  const form = useForm<Values>({
    resolver: zodResolver(schema) as Resolver<Values>,
    defaultValues: userDefaults(user),
  })
  useEffect(() => {
    if (open) form.reset(userDefaults(user))
  }, [open, user, form])

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      requireDirty={editing}
      title={editing ? t("platformUsers.edit", { name: user.username }) : t("platformUsers.create")}
      description={editing ? t("platformUsers.editDescription") : t("platformUsers.createDescription")}
      submitLabel={editing ? t("common.saveChanges") : t("platformUsers.create")}
      onSubmit={async (v) => {
        if (editing) {
          await update.mutateAsync({
            id: user.id,
            display_name: v.display_name,
            email: v.email || undefined,
            password: v.password || undefined,
            ...(self ? {} : { is_active: v.is_active, is_platform_admin: v.is_platform_admin }),
          })
        } else {
          if (!v.password) {
            form.setError("password", { message: t("platformUsers.passwordRequired") })
            return
          }
          await create.mutateAsync({
            username: v.username,
            password: v.password,
            display_name: v.display_name || undefined,
            email: v.email || undefined,
            is_platform_admin: v.is_platform_admin,
          })
        }
        onOpenChange(false)
      }}
    >
      <TextField
        control={form.control}
        name="username"
        label={t("members.form.username")}
        mono
        required
        readOnly={editing}
        autoFocus={!editing}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField control={form.control} name="display_name" label={t("members.form.displayName")} />
        <TextField control={form.control} name="email" type="email" label={t("members.form.email")} />
      </div>
      <TextField
        control={form.control}
        name="password"
        type="password"
        autoComplete="new-password"
        label={editing ? t("members.form.resetPassword") : t("members.form.password")}
        description={editing ? t("members.form.resetPasswordHint") : t("profile.password.rules")}
        required={!editing}
      />
      {editing && (
        <SwitchField
          control={form.control}
          name="is_active"
          variant="card"
          label={t("members.form.active")}
          description={self ? t("platformUsers.selfHint") : t("platformUsers.activeHint")}
          disabled={self}
        />
      )}
      <SwitchField
        control={form.control}
        name="is_platform_admin"
        variant="card"
        icon={ShieldCheck}
        label={t("members.form.platformAdmin")}
        description={self ? t("platformUsers.selfHint") : t("members.form.platformAdminHint")}
        disabled={self}
      />
    </FormSheet>
  )
}
